// Video Director step ④ panels: model picker, context/ref menu, reference set,
// final-frame generator and the insert-shot chooser. Presentational — every
// action is a callback; DirectorEditStep / VideoDirector own state and API calls.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import {
  LuChevronLeft,
  LuChevronRight,
  LuEllipsis,
  LuFilm,
  LuGalleryHorizontal,
  LuImagePlus,
  LuImages,
  LuInfo,
  LuLibrary,
  LuUpload,
  LuX,
} from "react-icons/lu";
import VideoThumb from "../../../timeline/IdeaTimeline/components/VideoThumb";
import { FPS, MAX_REFS, isStale } from "./edl";
import { PreviewButton, PreviewThumb } from "./ImagePreview";
import {
  FrameSlot,
  NoEndFrame,
  RefGrid,
  SlotPicker,
  SlotRow,
  ModelSelect,
  actClass,
} from "./FrameSlot";
import LibraryPicker from "./LibraryPicker";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tv = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.${key}`);

export const relativeTime = (iso) => {
  const sec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const [unit, size] = [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ].find(([, s]) => Math.abs(sec) >= s) || ["second", 1];
  return new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" }).format(
    Math.round(sec / size),
    unit
  );
};

const FILM_STEP = 24; // filmstrip thumb every 1 s
const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" };
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "13px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  background: "#fff",
  color: "#1f2937",
  lineHeight: "1.5",
  resize: "vertical",
};
const smallBtn = { fontSize: 12, padding: "6px 14px", borderRadius: "4px" };

// ── model picker ─────────────────────────────────────────────────────────────
const IMAGE_MODELS = ["gemini-nano", "gemini-pro-nano"];
const priceOf = (models, kind, id) => models?.[kind]?.[id];

/**
 * "veo" and "veo-fast" both run Gemini Omni since 2026-10-23 (only the resolution differs), so the picker shows
 * one "Omni": "veo-fast", or "veo" when only that one is priced.
 */
const withoutDuplicateOmni = (models, ids) => {
  if (!ids.includes("veo-fast") || !ids.includes("veo")) return ids;
  const fastUnpriced =
    priceOf(models, "video", "veo-fast") === null && priceOf(models, "video", "veo") !== null;
  const hidden = fastUnpriced ? "veo-fast" : "veo";
  return ids.filter((id) => id !== hidden);
};

/** Video models come from GET credit (`models.videoCaps`); a server that sends none keeps Omni (end frame, no refs). */
export const modelIds = (models, kind) =>
  kind === "image"
    ? IMAGE_MODELS
    : withoutDuplicateOmni(models, models?.videoCaps?.map((c) => c.id) ?? ["veo-fast", "veo"]);
export const capsOf = (models, id) =>
  models?.videoCaps?.find((c) => c.id === id) ?? { id, endFrame: true, maxRefs: 0 };

/** The model to send: the chosen one, or the first priced one when the choice has no price (null). */
export const effectiveModel = (models, kind, picked) => {
  // A saved "veo" pick reads as the one Omni shown ("veo-fast").
  const value =
    picked === "veo" && modelIds(models, kind).includes("veo-fast") ? "veo-fast" : picked;
  return priceOf(models, kind, value) === null
    ? (modelIds(models, kind).find((id) => priceOf(models, kind, id) !== null) ?? value)
    : value;
};

/** "Generate · 53 cr": the label with the credit price of the model that will run (none while unpriced). */
export const priced = (label, models, kind, model) => {
  const n = priceOf(models, kind, effectiveModel(models, kind, model));
  return typeof n === "number" ? `${label} · ${t("Models.PriceShort", { n })}` : label;
};

/** Inline error next to the action that failed (role=alert); the page-level banner uses it too. */
export function ErrorAlert({ text, onClose }) {
  return (
    <div
      role="alert"
      className="add-video-modal__error-alert"
      style={{
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "8px",
        padding: "12px 16px",
        margin: "12px 0",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <div style={{ flex: 1, fontSize: "13px", lineHeight: "1.5", color: "#991b1b" }}>{text}</div>
      {onClose && (
        <button
          type="button"
          aria-label={tv("Close")}
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            fontSize: "18px",
            color: "#dc2626",
            cursor: "pointer",
            padding: 0,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Segmented model selector with the credit price from GET credit (null price = unavailable). */
export function ModelPick({ kind, value, onChange, models }) {
  const current = effectiveModel(models, kind, value);
  if (modelIds(models, kind).length > 3)
    return <ModelSelect kind={kind} value={value} onChange={onChange} models={models} />;
  return (
    <div
      role="radiogroup"
      aria-label={t(`Models.${kind}`)}
      className="add-video-modal__director-models"
    >
      {modelIds(models, kind).map((id) => {
        const price = priceOf(models, kind, id);
        const active = current === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={price === null}
            className={`add-video-modal__ai-chip add-video-modal__director-model${active ? " add-video-modal__ai-chip--active" : ""}`}
            onClick={() => onChange(id)}
          >
            <span>{t(`Models.${id}`)}</span>
            {typeof price === "number" && (
              <span style={{ fontWeight: 400 }}>{t("Models.Price", { n: price })}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The old create screen's order (veo recommended first); unknown ids keep the server's order after these. */
const BRIEF_MODEL_ORDER = ["veo", "grok", "veo-fast", "seedance", "ltx"];
/** Priced video models for the brief row, read from the RAW caps (modelIds may fold duplicate Omni rows on some builds). */
export const briefModelIds = (models) => {
  const rank = (id) => {
    const i = BRIEF_MODEL_ORDER.indexOf(id);
    return i < 0 ? BRIEF_MODEL_ORDER.length : i;
  };
  return (models?.videoCaps?.map((c) => c.id) ?? ["veo-fast", "veo"])
    .filter((id) => typeof models?.video?.[id] === "number")
    .sort((a, b) => rank(a) - rank(b));
};

/** Brief row "AI model": one option per priced model ("Veo · 12 kr · Recommended") plus a muted caps hint. */
export function BriefModelRow({ models, value, onChange }) {
  const ids = briefModelIds(models);
  if (!ids.length) return null;
  const caps = capsOf(models, value);
  const hint = [caps.endFrame && t("Models.HintEnd"), caps.maxRefs > 0 && t("Models.HintRefs")]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 12,
        fontSize: 13,
      }}
    >
      <span style={{ ...labelStyle, marginBottom: 0 }}>{t("Models.BriefRow")}</span>
      <select
        className="add-video-modal__scene-prompt-textarea"
        style={{ ...inputStyle, width: "auto", minWidth: 240 }}
        aria-label={t("Models.BriefRow")}
        value={ids.includes(value) ? value : ids[0]}
        onChange={(e) => onChange(e.target.value)}
      >
        {ids.map((id) => (
          <option key={id} value={id}>
            {`${t(`Models.${id}`)} · ${t("Models.PriceShort", { n: models.video[id] })}${id === "veo" ? ` · ${t("Models.Recommended")}` : ""}`}
          </option>
        ))}
      </select>
      {hint && (
        <span title={hint} style={{ fontSize: 11, color: "#94a3b8" }}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ── context menu (labeled rows, IdeaTimeline's clip-menu look) ──────────────
/** Keep a w×h menu inside the viewport: flip up to yAlt when there is no room below, then clamp. */
export function clampMenu({ x, y, yAlt = y, w, h, vw, vh, pad = 8 }) {
  const top = y + h + pad > vh && yAlt - h >= pad ? yAlt - h : y;
  return {
    left: Math.max(pad, Math.min(x, vw - w - pad)),
    top: Math.max(pad, Math.min(top, vh - h - pad)),
  };
}

// Above the dialog band (pageModal 3000030, FileModal 3000040), below Lightbox 3000060.
export const MENU_Z = 3000050;

export function ActionMenu({ x, y, yAlt, actions, onClose }) {
  const box = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useLayoutEffect(() => {
    const { width: w, height: h } = box.current.getBoundingClientRect();
    setPos(clampMenu({ x, y, yAlt, w, h, vw: window.innerWidth, vh: window.innerHeight }));
  }, [x, y, yAlt]);
  // Keyboard: focus lands on the first item, arrows/Home/End move, Esc closes, focus returns to the trigger.
  useEffect(() => {
    const trigger = document.activeElement;
    box.current.querySelector("[role=menuitem]:not(:disabled)")?.focus();
    // Capture on document: Esc closes the menu only, never the Director/modal beneath it.
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onEsc, true);
    return () => {
      document.removeEventListener("keydown", onEsc, true);
      trigger?.focus?.();
    };
  }, []);
  const onKeyDown = (e) => {
    const items = [...box.current.querySelectorAll("[role=menuitem]:not(:disabled)")];
    const at = items.indexOf(document.activeElement);
    const to = {
      ArrowDown: items[(at + 1) % items.length],
      ArrowUp: items[(at - 1 + items.length) % items.length],
      Home: items[0],
      End: items[items.length - 1],
    }[e.key];
    if (to) {
      e.preventDefault();
      to.focus();
    }
  };
  // Portal to body: a transformed ancestor would otherwise become the fixed-position origin.
  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: MENU_Z }}
        onPointerDown={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={box}
        className="it-clip-context-menu it-clip-context-menu--labeled"
        role="menu"
        onKeyDown={onKeyDown}
        style={{ position: "fixed", ...pos, zIndex: MENU_Z + 1 }}
      >
        <div className="it-clip-context-menu__actions">
          {actions
            .filter((a) => !a.hidden)
            .map(({ key, Icon, label, onClick, disabled, title }) => (
              <button
                key={key}
                title={title}
                type="button"
                role="menuitem"
                className="it-clip-context-menu__icon-btn"
                disabled={disabled}
                onClick={() => {
                  onClose();
                  onClick();
                }}
              >
                <Icon size={15} />
                <span className="it-clip-context-menu__text">{label}</span>
              </button>
            ))}
          <span className="it-clip-context-menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="it-clip-context-menu__icon-btn"
            onClick={onClose}
          >
            <LuX size={15} />
            <span className="it-clip-context-menu__text">{t("Close")}</span>
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ── reference images ─────────────────────────────────────────────────────────
const refError = (r) =>
  t(
    r.error === "refs.too_large"
      ? "Errors.refs_too_large"
      : r.error === "content_filtered"
        ? "Errors.content_filtered"
        : "Errors.generic"
  );

/** "Adjusted for safety" chip; its ⓘ opens the original vs sent prompt inline (no modal). */
export function PolicyChip({ item }) {
  const [open, setOpen] = useState(false);
  const policy = item?.policy;
  if (!policy?.adjusted) return null;
  return (
    <div style={{ fontSize: 11, textAlign: "left" }}>
      <span
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "0 6px",
          borderRadius: 999,
          background: "#fef3c7",
          color: "#92400e",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setOpen((o) => !o);
          }
        }}
      >
        {t("PolicyAdjusted")}
        <LuInfo size={11} />
      </span>
      {open && (
        <div role="note" style={{ marginTop: 4, color: "#475569" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{t("PolicyOriginal")}</div>
              <div>{policy.originalPrompt}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{t("PolicySent")}</div>
              <div>{item.prompt}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A reference image: contain, spinner while pending, error text when failed. `preview` = { items, index,
 * click } adds the lightbox button (and opens on image click unless click === false, e.g. a picker).
 */
export function RefImage({ item, preview }) {
  const failed = item.status === "failed" || isStale(item);
  const ready = item.status === "ready" && item.url;
  return (
    <>
      <PreviewThumb
        preview={ready ? preview : null}
        clickable={preview?.click !== false}
        className="add-video-modal__vd-pv-wrap--fill"
      >
        <div className="add-video-modal__director-ref-img">
          {ready && <img src={item.url} alt="" />}
          {item.status === "pending" && !failed && (
            <span className="add-video-modal__scene-spinner" />
          )}
          {failed && (
            <span style={{ fontSize: 11, color: "#ef4444", padding: 4 }}>{refError(item)}</span>
          )}
        </div>
      </PreviewThumb>
      <PolicyChip item={item} />
    </>
  );
}

export const useFilePick = (onFile) => {
  const ref = useRef(null);
  const input = (label) => (
    <input
      ref={ref}
      type="file"
      accept="image/*"
      hidden
      style={{ display: "none" }}
      aria-label={label}
      onChange={(e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) onFile(file);
      }}
    />
  );
  return [() => ref.current?.click(), input];
};

/** The Reference set strip: add frame / upload, thumbnails with a per-ref menu. */
export function RefStrip({
  refs,
  showAddFrame = true,
  canAddFrame,
  uploading,
  onAddFrame,
  onUpload,
  actionsFor,
}) {
  const [menu, setMenu] = useState(null); // { x, y, ref }
  // Lightbox collection: the ready refs, with the menu's non-destructive actions.
  const ready = refs.filter((r) => r.status === "ready" && r.url);
  const previews = ready.map((r) => ({
    url: r.url,
    name: t("RefN", { n: refs.indexOf(r) + 1 }),
    description: r.prompt,
    actions: actionsFor(r)
      .filter((a) => !a.hidden && a.key !== "delete")
      .map((a) => ({
        key: a.key,
        label: a.label,
        icon: a.Icon,
        disabled: a.disabled,
        onClick: a.onClick,
      })),
  }));
  const [pickFile, fileInput] = useFilePick((file) => onUpload(file));
  return (
    <div className="add-video-modal__director-refs" role="group" aria-label={t("RefSetName")}>
      <div className="add-video-modal__director-refs-head">
        <span style={{ ...labelStyle, marginBottom: 0 }}>
          {t("RefSet", { n: refs.length, max: MAX_REFS })}
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {showAddFrame && (
            <button
              type="button"
              className="add-video-modal__btn add-video-modal__btn--secondary"
              style={{ fontSize: 12, padding: "4px 12px", display: "flex", gap: 4 }}
              disabled={!canAddFrame}
              onClick={onAddFrame}
            >
              <LuImagePlus size={12} />
              {t("AddCurrentFrame")}
            </button>
          )}
          {onUpload && (
            <>
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--ghost"
                style={{ fontSize: 12, padding: "4px 12px", display: "flex", gap: 4 }}
                disabled={uploading || refs.length >= MAX_REFS}
                onClick={pickFile}
              >
                <LuUpload size={12} />
                {t("Upload")}
              </button>
              {fileInput(t("UploadImage"))}
            </>
          )}
        </div>
      </div>
      {refs.length === 0 ? (
        <div style={{ fontSize: 12, color: "#64748b" }}>{t("RefSetEmpty")}</div>
      ) : (
        <div className="add-video-modal__director-refs-list">
          {refs.map((r, i) => (
            <div
              key={r.id}
              className="add-video-modal__director-ref"
              data-ref-id={r.id}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, ref: r });
              }}
            >
              <RefImage item={r} preview={{ items: previews, index: ready.indexOf(r) }} />
              <button
                type="button"
                className="add-video-modal__director-ref-menu"
                aria-label={`${t("RefMenu")} ${i + 1}`}
                onClick={(e) => {
                  const b = e.currentTarget.getBoundingClientRect();
                  setMenu({ x: b.left, y: b.bottom, yAlt: b.top, ref: r });
                }}
              >
                <LuEllipsis size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {menu && (
        <ActionMenu
          x={menu.x}
          y={menu.y}
          yAlt={menu.yAlt}
          actions={actionsFor(menu.ref)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

// ── overlay panels ───────────────────────────────────────────────────────────
/**
 * A panel's fields, saved in localStorage per project + panel (`vd:draft:<projectId>:<panel>`), restored
 * on reopen (also after a reload) and cleared on a successful submit. `fresh` ignores a stored draft
 * (a panel opened prefilled from somewhere). Returns [value, patch, clear, discard, dirty].
 */
export function useDraft(projectId, panel, initial, fresh = false) {
  const key = `vd:draft:${projectId}:${panel}`;
  const first = useRef(initial);
  const skip = useRef(false);
  const [value, setValue] = useState(() => {
    if (fresh) return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? { ...initial, ...JSON.parse(raw) } : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (skip.current || value === first.current) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode / quota: the draft just is not kept */
    }
  }, [key, value]);
  const patch = (p) => setValue((v) => ({ ...v, ...(typeof p === "function" ? p(v) : p) }));
  const clear = () => {
    skip.current = true;
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* nothing to clear */
    }
  };
  const discard = () => {
    clear();
    skip.current = false;
    setValue(first.current);
  };
  return [value, patch, clear, discard, value !== first.current];
}

/**
 * Overlay panel over the right two thirds of the Director area (min 720 px; full width when narrow). The
 * translucent scrim over the rest and Esc return to the editor; the sticky action row holds `actions`.
 */
export function OverlayPanel({
  title,
  helper,
  onClose,
  actions,
  onDiscard,
  error,
  onDismissError,
  className,
  children,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    (
      el.querySelector("textarea") ||
      el.querySelector("input:not([type=radio]):not([type=file]):not([type=checkbox]), select") ||
      el.querySelector("button")
    )?.focus();
  }, []);
  return (
    <div className="add-video-modal__vd-overlay">
      <div className="add-video-modal__vd-scrim" role="presentation" onClick={onClose} />
      <aside
        ref={ref}
        className={`add-video-modal__vd-overlay-panel${className ? ` ${className}` : ""}`}
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          e.stopPropagation();
          onClose();
        }}
      >
        <div className="add-video-modal__vd-overlay-head">
          <div style={{ minWidth: 0 }}>
            <div className="add-video-modal__vd-drawer-title" style={{ padding: 0, border: 0 }}>
              {title}
            </div>
            {helper && <div className="add-video-modal__vd-overlay-helper">{helper}</div>}
          </div>
          <button
            type="button"
            className="app-modal__toggle-btn"
            aria-label={tv("Close")}
            title={tv("Close")}
            onClick={onClose}
          >
            <LuX size={16} />
          </button>
        </div>
        <div className="add-video-modal__vd-overlay-body">
          {children}
          {error && <ErrorAlert text={error} onClose={onDismissError} />}
        </div>
        <div className="add-video-modal__vd-overlay-actions">
          {onDiscard && (
            <button
              type="button"
              className="add-video-modal__scene-edit-btn"
              style={{ marginRight: "auto" }}
              onClick={onDiscard}
            >
              {t("DiscardDraft")}
            </button>
          )}
          {actions}
        </div>
      </aside>
    </div>
  );
}

const actionRow = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };

/** Prompt + image model → a new reference image from a ref or a video frame. */
export function FinalFramePanel({
  projectId,
  source,
  initialPrompt = "",
  models,
  model,
  onModel,
  busy,
  onGenerate,
  onClose,
  error,
  onDismissError,
}) {
  const [d, patch, clear, discard, dirty] = useDraft(
    projectId,
    "final",
    { prompt: initialPrompt },
    !!initialPrompt
  );
  const prompt = d.prompt;
  return (
    <OverlayPanel
      title={t("FinalTitle")}
      helper={t("FinalHelper")}
      onClose={onClose}
      onDiscard={dirty ? discard : undefined}
      error={error}
      onDismissError={onDismissError}
      actions={
        <div style={actionRow}>
          <ModelPick kind="image" value={model} onChange={onModel} models={models} />
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--primary"
            style={smallBtn}
            disabled={busy || !prompt.trim()}
            onClick={() => {
              clear();
              onGenerate(prompt.trim());
            }}
          >
            {priced(t("Generate"), models, "image", model)}
          </button>
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--ghost"
            style={smallBtn}
            onClick={onClose}
          >
            {tv("Cancel")}
          </button>
        </div>
      }
    >
      <div className="add-video-modal__vd-cols">
        <div className="add-video-modal__vd-col">{source}</div>
        <div className="add-video-modal__vd-col">
          <textarea
            className="add-video-modal__scene-prompt-textarea"
            style={inputStyle}
            rows={6}
            maxLength={1000}
            aria-label={t("FinalPrompt")}
            placeholder={t("FinalPrompt")}
            value={prompt}
            onChange={(e) => patch({ prompt: e.target.value })}
          />
        </div>
      </div>
    </OverlayPanel>
  );
}

// ── insert shot ──────────────────────────────────────────────────────────────
export const nudgeBtn = (label, Icon, onClick, disabled) => (
  <button
    type="button"
    className="it-btn it-btn--icon"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
  >
    <Icon size={16} />
  </button>
);

/**
 * Insert a generated shot at the playhead. Start slot = the playhead frame (fixed; ◀ ▶ move it one
 * frame); end slot = empty (AI decides) | next clip | reference set / keyframe / upload | a frame of a clip.
 * Same FrameSlot look as Add shot; footer: model select, the one primary action, Cancel.
 */
export function InsertPanel({
  projectId,
  nextRequired,
  canInsert,
  anchorTake,
  startFrame,
  startLabel,
  playhead,
  total,
  onNudge,
  afterTake,
  afterFrame,
  refs,
  keyframes = [],
  pickable,
  models,
  model,
  onModel,
  uploading,
  onUpload,
  onRefUrl,
  onClose,
  onGenerate,
  error,
  onDismissError,
}) {
  const [d, patch, clear, discard, dirty] = useDraft(projectId, "insert", {
    text: "",
    mode: "auto",
    refId: null,
    kfLabel: null, // set when the ref came from a storyboard keyframe
    pickTakeId: null,
    pickFrame: 0, // take-local
  });
  const [picking, setPicking] = useState(null); // ref | kf | pick
  const text = d.text;
  const [pickFile, fileInput] = useFilePick(async (file) => {
    const id = await onUpload(file);
    if (id) patch({ mode: "ref", refId: id, kfLabel: null });
  });
  const pickEntry = pickable.find((x) => x.tk.id === d.pickTakeId) || pickable[0];
  const pickTake = pickEntry?.tk;
  const lastPick = Math.max(0, (pickTake?.frames || 1) - 1);
  const frame = Math.min(d.pickFrame, lastPick);
  const chosenRef = refs.find((r) => r.id === d.refId);
  const refReady = chosenRef?.status === "ready" && !!chosenRef.url;
  const readyRefs = refs.filter((r) => r.status === "ready" && r.url);
  const refName = (r) => t("RefN", { n: refs.indexOf(r) + 1 });
  const refEntries = refs
    .filter((r) => r.status !== "failed")
    .map((r) => ({ key: `r${r.id}`, item: r, label: refName(r) }));
  const kfEntries = keyframes
    .filter((k) => k.item?.status === "ready" && k.item.url)
    .map((k) => ({
      key: `k${k.id}`,
      item: k.item,
      label: t("KeyframeOf", { label: k.label }),
      kf: k.label,
    }));
  const endOk = capsOf(models, effectiveModel(models, "video", model)).endFrame;
  // A restored draft may name an option that is not available any more.
  const avail = {
    auto: true,
    next: !!afterTake?.url,
    ref: !!chosenRef || readyRefs.length > 0,
    pick: pickable.length > 0,
  };
  const mode = endOk && avail[d.mode] ? d.mode : "auto";
  const endFrame =
    mode === "pick"
      ? { mode, takeId: pickTake?.id, frame }
      : mode === "ref"
        ? { mode, refId: d.refId }
        : { mode };
  const blocked =
    !canInsert ||
    (nextRequired && !text.trim()) ||
    (mode === "ref" && !refReady) ||
    (mode === "pick" && !pickTake);

  // One-image lightbox collections for the start / end frame.
  const frameItem = (take, f, name) => [
    { url: take.url, kind: "video", seekSec: (f + 0.5) / FPS, name },
  ];
  const videoFrame = (take, f, name) => (
    <PreviewThumb
      className="add-video-modal__vd-pv-wrap--fill"
      preview={{ items: frameItem(take, f, name), index: 0 }}
      clickable
    >
      <VideoThumb
        url={take.url}
        seekSec={(f + 0.5) / FPS}
        className="add-video-modal__director-frame-large"
      />
    </PreviewThumb>
  );
  const clipItems = pickable.map(({ tk, label }) => ({ url: tk.url, kind: "video", name: label }));
  const filmFrames = pickTake
    ? Array.from({ length: Math.ceil(pickTake.frames / FILM_STEP) }, (_, i) => i * FILM_STEP)
    : [];
  const filmItems = filmFrames.map((f) => ({
    url: pickTake.url,
    kind: "video",
    seekSec: (f + 0.5) / FPS,
    name: `${t("PickFrame")} ${f}`,
  }));

  let end = {};
  if (mode === "next")
    end = {
      sourceLabel: t("BridgeLabel"),
      visual: videoFrame(afterTake, afterFrame, t("BridgeLabel")),
    };
  else if (mode === "ref" && chosenRef) {
    const name = d.kfLabel ? t("KeyframeOf", { label: d.kfLabel }) : refName(chosenRef);
    end = {
      sourceLabel: name,
      note: d.refId && !refReady ? t("RefPending") : undefined,
      visual: (
        <RefImage
          item={chosenRef}
          preview={
            refReady
              ? { items: [{ url: chosenRef.url, name, description: chosenRef.prompt }], index: 0 }
              : null
          }
        />
      ),
    };
  } else if (mode === "pick" && pickTake)
    end = {
      sourceLabel: t("FrameOf", { label: pickEntry.label, time: `${(frame / FPS).toFixed(2)} s` }),
      visual: videoFrame(pickTake, frame, t("EndFrame")),
    };

  const sources = [
    {
      id: "ref",
      label: t("SrcRefSet"),
      Icon: LuImages,
      disabled: readyRefs.length === 0,
      onSelect: () => setPicking("ref"),
    },
    {
      id: "kf",
      label: t("SrcKeyframes"),
      Icon: LuFilm,
      disabled: kfEntries.length === 0 || !onRefUrl,
      onSelect: () => setPicking("kf"),
    },
    onUpload && {
      id: "up",
      label: t("SrcUpload"),
      Icon: LuUpload,
      disabled: uploading || refs.length >= MAX_REFS,
      onSelect: pickFile,
    },
    onRefUrl && {
      id: "lib",
      label: t("SrcLibrary"),
      Icon: LuLibrary,
      onSelect: () => setPicking("lib"),
    },
    afterTake?.url && {
      id: "next",
      label: t("BridgeNext"),
      Icon: LuFilm,
      onSelect: () => patch({ mode: "next" }),
    },
    {
      id: "pick",
      label: t("EndPick"),
      Icon: LuGalleryHorizontal,
      disabled: pickable.length === 0,
      onSelect: () => {
        patch({ mode: "pick" });
        setPicking("pick");
      },
    },
  ].filter(Boolean);

  let picker = null;
  if (picking === "lib") {
    picker = (
      <SlotPicker title={`${t("EndFrame")} · ${t("SrcLibrary")}`} onDone={() => setPicking(null)}>
        <LibraryPicker
          kind="image"
          onPick={async ({ url }) => {
            setPicking(null);
            const id = await onRefUrl(url);
            if (id) patch({ mode: "ref", refId: id, kfLabel: null });
          }}
        />
      </SlotPicker>
    );
  } else if (picking === "ref" || picking === "kf") {
    const kf = picking === "kf";
    picker = (
      <SlotPicker
        title={`${t("EndFrame")} · ${kf ? t("SrcKeyframes") : t("SrcRefSet")}`}
        onDone={() => setPicking(null)}
      >
        <RefGrid
          entries={kf ? kfEntries : refEntries}
          isOn={(e) => !kf && d.mode === "ref" && d.refId === e.item.id}
          onPick={async (e) => {
            if (!kf) patch({ mode: "ref", refId: e.item.id, kfLabel: null });
            else {
              const id = await onRefUrl(e.item.url);
              if (id) patch({ mode: "ref", refId: id, kfLabel: e.kf });
            }
            setPicking(null);
          }}
        />
      </SlotPicker>
    );
  } else if (picking === "pick" && pickTake) {
    picker = (
      <SlotPicker title={`${t("EndFrame")} · ${t("EndPick")}`} onDone={() => setPicking(null)}>
        <div className="add-video-modal__director-clips" role="group" aria-label={t("PickTake")}>
          {pickable.map((pk) => {
            const { tk, label } = pk;
            return (
              <button
                key={tk.id}
                type="button"
                aria-pressed={tk.id === pickTake.id}
                className={`add-video-modal__director-clip${tk.id === pickTake.id ? " add-video-modal__director-clip--on" : ""}`}
                onClick={() => patch({ pickTakeId: tk.id, pickFrame: 0 })}
              >
                <VideoThumb
                  url={tk.url}
                  seekSec={0.5}
                  className="add-video-modal__director-clip-thumb"
                />
                <span>{label}</span>
                <PreviewButton items={clipItems} index={pickable.indexOf(pk)} />
              </button>
            );
          })}
        </div>
        <div
          className="add-video-modal__director-filmstrip"
          role="group"
          aria-label={t("PickFrame")}
        >
          {filmFrames.map((f) => (
            <button
              key={f}
              type="button"
              aria-label={`${t("PickFrame")} ${f}`}
              aria-pressed={Math.floor(frame / FILM_STEP) * FILM_STEP === f}
              className={`add-video-modal__director-film${Math.floor(frame / FILM_STEP) * FILM_STEP === f ? " add-video-modal__director-film--on" : ""}`}
              onClick={() => patch({ pickFrame: f })}
            >
              <VideoThumb
                url={pickTake.url}
                seekSec={(f + 0.5) / FPS}
                className="add-video-modal__director-film-thumb"
              />
              <PreviewButton items={filmItems} index={filmFrames.indexOf(f)} />
            </button>
          ))}
        </div>
        <div className="add-video-modal__director-nudge">
          {nudgeBtn(
            t("PrevFrame"),
            LuChevronLeft,
            () => patch({ pickFrame: Math.max(0, frame - 1) }),
            frame <= 0
          )}
          <span>{(frame / FPS).toFixed(2)} s</span>
          {nudgeBtn(
            t("NextFrame"),
            LuChevronRight,
            () => patch({ pickFrame: Math.min(lastPick, frame + 1) }),
            frame >= lastPick
          )}
        </div>
      </SlotPicker>
    );
  }

  const what = nextRequired ? t("WhatNextRequired") : t("WhatNext");
  return (
    <OverlayPanel
      className="add-video-modal__vd-overlay-panel--add"
      title={t("InsertTitle")}
      helper={t("InsertHelper")}
      onClose={onClose}
      onDiscard={dirty ? discard : undefined}
      error={error}
      onDismissError={onDismissError}
      actions={
        <>
          <ModelSelect kind="video" value={model} onChange={onModel} models={models} />
          <button
            type="button"
            className={actClass("primary")}
            disabled={blocked}
            onClick={() => {
              clear();
              onGenerate(text, endFrame);
            }}
          >
            {priced(t("Generate"), models, "video", model)}
          </button>
          <button type="button" className={actClass("ghost")} onClick={onClose}>
            {tv("Cancel")}
          </button>
        </>
      }
    >
      <SlotRow
        picker={picker}
        start={
          <FrameSlot
            name="start"
            label={t("StartFrame")}
            visual={
              anchorTake?.url ? videoFrame(anchorTake, startFrame, t("StartFrame")) : <span />
            }
            footer={
              <>
                <span className="add-video-modal__fs-src">{t("PlayheadFrame")}</span>
                {nudgeBtn(t("PrevFrame"), LuChevronLeft, () => onNudge(-1), playhead <= 1)}
                <span style={{ fontSize: 12, color: "#475569" }}>{startLabel}</span>
                {nudgeBtn(t("NextFrame"), LuChevronRight, () => onNudge(1), playhead >= total)}
              </>
            }
          />
        }
        end={
          endOk ? (
            <FrameSlot
              name="end"
              label={t("EndFrame")}
              addLabel={t("AddEndFrame")}
              hint={t("EndEmptyHintAI")}
              visual={end.visual}
              sourceLabel={end.sourceLabel}
              note={end.note}
              sources={sources}
              onRemove={() => patch({ mode: "auto", refId: null, kfLabel: null })}
              extra={
                mode === "pick" ? (
                  <button
                    type="button"
                    className="add-video-modal__fs-link"
                    onClick={() => setPicking("pick")}
                  >
                    {t("PickFrame")}
                  </button>
                ) : null
              }
            />
          ) : (
            <NoEndFrame />
          )
        }
      />
      {onUpload && fileInput(t("UploadImage"))}
      <div className="add-video-modal__fs-field">
        <div className="add-video-modal__fs-label">{what}</div>
        <textarea
          className="add-video-modal__scene-prompt-textarea"
          style={inputStyle}
          rows={3}
          maxLength={500}
          aria-label={what}
          value={text}
          onChange={(e) => patch({ text: e.target.value })}
        />
      </div>
    </OverlayPanel>
  );
}
